import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Card, Alert, Button, Badge } from 'react-bootstrap';
import axiosInstance from '../api/axiosInstance';
import config from '../config';
import './LocationDetail.css';
import { useAuth } from '../context/AuthContext';
import LeaderboardList from '../components/LeaderboardList';

const normalizeMediaUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  if (rawUrl.startsWith('http')) return rawUrl;

  const base = config.API_BASE?.replace(/\/$/, '') || window.location.origin;
  return new URL(rawUrl, base).toString();
};

const collectPhotoItems = (photosField) => {
  if (!photosField) return [];

  if (Array.isArray(photosField)) {
    return photosField;
  }

  if (Array.isArray(photosField?.data)) {
    return photosField.data;
  }

  if (photosField?.data && typeof photosField.data === 'object') {
    return [photosField.data];
  }

  if (typeof photosField === 'object') {
    return [photosField];
  }

  return [];
};

const toGalleryPhotos = (photosField) => {
  const photoItems = collectPhotoItems(photosField);

  return photoItems
    .map((item, index) => {
      const media = item?.attributes || item;
      const formats = media?.formats || media?.attributes?.formats;
      const originalUrl = media?.url || media?.attributes?.url;
      const thumbnailUrl =
        formats?.small?.url ||
        formats?.thumbnail?.url ||
        formats?.medium?.url ||
        originalUrl;

      const fullImage = normalizeMediaUrl(originalUrl || '');
      const thumbnail = normalizeMediaUrl(thumbnailUrl || '');

      if (!fullImage && !thumbnail) {
        return null;
      }

      // attempt to extract uploader and createdAt from several possible shapes
      let uploaderName = null;
      const createdBy = media?.createdBy || media?.attributes?.createdBy || item?.attributes?.createdBy || item?.createdBy;
      if (createdBy) {
        if (typeof createdBy === 'string' || typeof createdBy === 'number') {
          uploaderName = String(createdBy);
        } else if (createdBy?.username) {
          uploaderName = createdBy.username;
        } else if (createdBy?.data?.attributes?.username) {
          uploaderName = createdBy.data.attributes.username;
        } else if (createdBy?.data?.username) {
          uploaderName = createdBy.data.username;
        } else if (createdBy?.attributes?.username) {
          uploaderName = createdBy.attributes.username;
        }
      }

      const uploadedAt = media?.createdAt || media?.attributes?.createdAt || item?.attributes?.createdAt || item?.createdAt || null;

      return {
        id: media?.id || item?.id || index,
        name: media?.name || media?.alternativeText || `location-photo-${index + 1}`,
        thumbnail: thumbnail || fullImage,
        fullImage: fullImage || thumbnail,
        uploaderId:
          createdBy?.id ||
          createdBy?.data?.id ||
          createdBy?.attributes?.id ||
          null,
        uploaderName,
        uploadedAt,
      };
    })
    .filter(Boolean);
};

const getCoverPhotoUrl = (coverPhotoField) => {
  if (!coverPhotoField) return null;

  const media = coverPhotoField?.attributes || coverPhotoField?.data?.attributes || coverPhotoField;
  const formats = media?.formats || {};
  const originalUrl = media?.url || '';

  const imageUrl = formats?.large?.url || formats?.medium?.url || formats?.small?.url || originalUrl;
  return normalizeMediaUrl(imageUrl || '');
};

const LocationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activePhotoIndex, setActivePhotoIndex] = useState(-1);
  const [userDistanceMeters, setUserDistanceMeters] = useState(null);
  const [canCheckin, setCanCheckin] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkinCount, setCheckinCount] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const auth = useAuth();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [deletingPhoto, setDeletingPhoto] = useState(false);

  const handleBack = () => {
    const mapReturnPath = routeLocation.state?.mapReturnPath;

    if (mapReturnPath) {
      navigate(mapReturnPath);
      return;
    }

    navigate(-1);
  };

  const fetchLocation = async () => {
    setLoading(true);
    try {
      // Strapi v5 uses documentId in /:id endpoints; fallback for legacy numeric id links.
      const response = await axiosInstance.get(
        `/locations/${id}?populate[photos]=true&populate[coverPhoto]=true`
      );
      setLocation(response.data?.data || null);
    } catch (err) {
      const isNumericId = /^\d+$/.test(String(id || ''));

      if (isNumericId) {
        try {
            const fallbackResponse = await axiosInstance.get(
              `/locations?filters[id][$eq]=${id}&pagination[pageSize]=1&populate[photos]=true&populate[coverPhoto]=true`
            );
          const fallbackItem = fallbackResponse.data?.data?.[0] || null;

          if (fallbackItem) {
            setLocation(fallbackItem);
            return;
          }
        } catch (fallbackErr) {
          console.error('Fallback location fetch failed:', fallbackErr);
        }
      }

      setError('Failed to load location details');
      console.error('Error fetching location:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocation();
  }, [id]);

  const fetchCheckinCount = async (targetLocationId) => {
    const resolvedLocationId = targetLocationId || location?.id;
    if (!resolvedLocationId) return;
    try {
      const resp = await axiosInstance.get(`/checkins/count?location=${resolvedLocationId}`);
      const total = resp.data?.data?.total;
      setCheckinCount(typeof total === 'number' ? total : null);
    } catch (e) {
      console.error('Failed to fetch checkin count', e);
    }
  };

  useEffect(() => {
    fetchCheckinCount();
  }, [location?.id]);

  const fetchTopUsers = async (targetLocationId) => {
    const resolvedLocationId = targetLocationId || location?.id;
    if (!resolvedLocationId) return;
    try {
      const resp = await axiosInstance.get(`/checkins/top?location=${resolvedLocationId}&limit=10`);
      setTopUsers(resp.data?.data || []);
    } catch (e) {
      console.error('Failed to fetch top users', e);
    }
  };

  useEffect(() => {
    fetchTopUsers();
  }, [location?.id]);

  useEffect(() => {
    // compute user distance to location (if available)
    if (!location?.latitude || !location?.longitude || !navigator?.geolocation) return;

    const computeDistance = (lat1, lon1, lat2, lon2) => {
      const toRad = (v) => (v * Math.PI) / 180;
      const R = 6371000;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    };

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        const dist = computeDistance(lat, lon, Number(location.latitude), Number(location.longitude));
        setUserDistanceMeters(dist);
        const maxDist = Number(config.CHECKIN_MAX_DISTANCE_METERS) || 500;
        setCanCheckin(auth?.isAuthenticated && dist < maxDist && location.categoryType !== 'alert');
      },
      (err) => {
        console.warn('Geolocation failed:', err && err.message);
        setUserDistanceMeters(null);
        setCanCheckin(false);
      },
      { enableHighAccuracy: true, maximumAge: 60 * 1000 }
    );
  }, [location?.id, auth?.isAuthenticated]);

  const galleryPhotos = toGalleryPhotos(location?.photos);
  const coverPhotoUrl = getCoverPhotoUrl(location?.coverPhoto);

  const isLightboxOpen = activePhotoIndex >= 0 && activePhotoIndex < galleryPhotos.length;
  const activePhoto = isLightboxOpen ? galleryPhotos[activePhotoIndex] : null;
  const canDeleteActivePhoto = !!auth?.isAuthenticated && !!activePhoto;

  const openLightbox = (index) => {
    setActivePhotoIndex(index);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
    setUploadError('');
  };

  const handleUpload = async () => {
    if (!auth?.isAuthenticated) {
      alert('請先登入以上傳相片');
      return;
    }

    if (!selectedFiles || selectedFiles.length === 0) {
      alert('請先選擇要上傳的相片');
      return;
    }

    const form = new FormData();
    selectedFiles.forEach((f) => form.append('photos', f));

    setUploading(true);
    setUploadError('');
    try {
      const resp = await axiosInstance.post(`/locations/${location.id}/add-photos`, form);
      if (resp?.data) {
        // refresh location data
        await fetchLocation();
        await Promise.all([
          fetchCheckinCount(location.id),
          fetchTopUsers(location.id),
        ]);
        setSelectedFiles([]);
        alert('上傳成功');
      } else {
        setUploadError('上傳失敗');
      }
    } catch (err) {
      console.error('Upload failed', err);
      const msg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || '上傳失敗';
      setUploadError(msg);
      alert(msg);
    } finally {
      setUploading(false);
    }
  };

  const closeLightbox = () => {
    setActivePhotoIndex(-1);
  };

  const handleDeletePhoto = async () => {
    if (!activePhoto?.id || !location?.id) return;
    if (!auth?.isAuthenticated) {
      alert('請先登入');
      return;
    }

    const ok = window.confirm('確定要刪除此相片？');
    if (!ok) return;

    setDeletingPhoto(true);
    try {
      await axiosInstance.delete(`/locations/${location.id}/photos/${activePhoto.id}`);
      closeLightbox();
      await fetchLocation();
    } catch (err) {
      console.error('Delete photo failed', err);
      const msg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || '刪除失敗';
      alert(msg);
    } finally {
      setDeletingPhoto(false);
    }
  };

  const showPrevPhoto = () => {
    if (!galleryPhotos.length) return;
    setActivePhotoIndex((prevIndex) => (prevIndex - 1 + galleryPhotos.length) % galleryPhotos.length);
  };

  const showNextPhoto = () => {
    if (!galleryPhotos.length) return;
    setActivePhotoIndex((prevIndex) => (prevIndex + 1) % galleryPhotos.length);
  };

  useEffect(() => {
    if (!isLightboxOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeLightbox();
      }
      if (e.key === 'ArrowLeft') {
        showPrevPhoto();
      }
      if (e.key === 'ArrowRight') {
        showNextPhoto();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLightboxOpen, galleryPhotos.length]);

  if (loading) {
    return <div className="text-center p-5">Loading...</div>;
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={handleBack}>返回</Button>
      </Container>
    );
  }

  if (!location) {
    return (
      <Container className="py-5">
        <Alert variant="warning">Location not found</Alert>
      </Container>
    );
  }

  const isAlert = location.categoryType === 'alert';

  return (
    <Container className="location-detail-container py-5">
      <Button variant="outline-secondary" onClick={handleBack} className="mb-4">
        ← 返回
      </Button>

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h2 className="d-inline-block me-3">{location.nameTc || location.nameEn}</h2>
              {!isAlert && auth?.isAuthenticated && (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!canCheckin || checkingIn}
                  onClick={async () => {
                    if (!auth?.isAuthenticated) return;
                    setCheckingIn(true);
                    try {
                      const posPromise = new Promise((resolve, reject) => {
                        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true });
                      });
                      const pos = await posPromise;
                      const payload = {
                        location: location.id,
                        latitude: pos.coords.latitude,
                        longitude: pos.coords.longitude,
                        source: 'web',
                      };
                      const resp = await axiosInstance.post('/checkins', payload);
                      if (resp?.data?.data) {
                        setCheckinCount((c) => (typeof c === 'number' ? c + 1 : c));
                        await fetchTopUsers(); // 更新排行榜
                        alert('簽到成功！');
                      } else {
                        alert('簽到可能失敗');
                      }
                    } catch (err) {
                      console.error('Checkin failed', err);
                      const message = err?.response?.data?.error?.message || err?.message || '簽到失敗';
                      alert(message);
                    } finally {
                      setCheckingIn(false);
                    }
                  }}
                >
                  {checkingIn ? '簽到中…' : '簽到'}
                </Button>
              )}
              {auth?.isAuthenticated && (
                <div className="mt-2">
                  使用者距離：{userDistanceMeters == null ? '無法取得' : `${Math.round(userDistanceMeters)} m`}
                </div>
              )}
              {isAlert && (
                <Badge bg="warning" text="dark" className="mt-2">
                  提醒：{location.alertCategory?.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>
          </div>

          <hr />

          <div className="location-info">
            {coverPhotoUrl && (
              <div className="info-group">
                <div className="cover-photo-container mb-4">
                  <img
                    src={coverPhotoUrl}
                    alt="Cover photo"
                    className="img-fluid rounded"
                    style={{ maxHeight: '400px', width: '100%', objectFit: 'cover' }}
                  />
                </div>
              </div>
            )}

            <div className="info-group">
              <h5>相片</h5>

              <div className="upload-photos mb-3">
                <div className="upload-controls d-flex align-items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    disabled={!auth?.isAuthenticated || uploading}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleUpload}
                    disabled={!auth?.isAuthenticated || uploading || selectedFiles.length === 0}
                  >
                    {uploading ? '上傳中…' : '上傳'}
                  </Button>
                  <small className="text-muted">每張 5MB（僅 JPG）</small>
                </div>
                {!auth?.isAuthenticated && (
                  <div className="text-muted mt-2">請先登入以上傳相片。</div>
                )}
                {uploadError && <div className="text-danger mt-2">{uploadError}</div>}
              </div>

              {galleryPhotos.length > 0 ? (
                <div className="location-gallery-grid">
                  {galleryPhotos.map((photo, index) => (
                    <button
                      key={photo.id}
                      type="button"
                      className="gallery-thumb"
                      onClick={() => openLightbox(index)}
                      aria-label={`查看圖片 ${photo.name}`}
                    >
                      <img src={photo.thumbnail} alt={photo.name} loading="lazy" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-muted">目前尚未有相片。</div>
              )}
            </div>

            {(location.addressTc || location.addressEn) && (
              <div className="info-group">
                <h5>地址</h5>
                <p>{location.addressTc || location.addressEn}</p>
              </div>
            )}

            {(location.districtTc || location.districtEn) && (
              <div className="info-group">
                <h5>地區</h5>
                <p>{location.districtTc || location.districtEn}</p>
              </div>
            )}

            {(location.facilityTypeTc || location.facilityTypeEn) && !isAlert && (
              <div className="info-group">
                <h5>設施類型</h5>
                <p>{location.facilityTypeTc || location.facilityTypeEn}</p>
              </div>
            )}

            {isAlert && (location.alertDetailsEn || location.alertDetailsTc) && (
              <div className="info-group">
                <h5>詳細信息</h5>
                <p>{location.alertDetailsTc || location.alertDetailsEn}</p>
              </div>
            )}

            {(location.openingHoursTc || location.openingHoursEn) && !isAlert && (
              <div className="info-group">
                <h5>營業時間</h5>
                <p>{location.openingHoursTc || location.openingHoursEn}</p>
              </div>
            )}

            {location.telephone && (
              <div className="info-group">
                <h5>電話</h5>
                <p>
                  <a href={`tel:${location.telephone}`}>{location.telephone}</a>
                </p>
              </div>
            )}

            {(location.websiteTc || location.websiteEn) && !isAlert && (
              <div className="info-group">
                <h5>網站</h5>
                <p>
                  <a href={location.websiteTc || location.websiteEn} target="_blank" rel="noopener noreferrer">
                    {location.websiteTc || location.websiteEn}
                  </a>
                </p>
              </div>
            )}

            {location.latitude && location.longitude && (
              <div className="info-group">
                <h5>座標</h5>
                <p>
                  {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  {' '}
                  <a
                    href={`https://maps.google.com/?q=${location.latitude},${location.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    在 Google 地圖中查看
                  </a>
                </p>
              </div>
            )}

            {location.source && (
              <div className="info-group">
                <small className="text-muted">來源：{location.source}</small>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      <div className="mt-3">
        {checkinCount !== null && (
          <div className="mb-2">總簽到次數：<strong>{checkinCount}</strong></div>
        )}

        <div className="location-leaderboard-wrap mb-3">
          <LeaderboardList
            title="地點前 10 名簽到用戶"
            items={topUsers}
            emptyText="此地點目前尚無簽到資料"
            compact
          />
        </div>

        {!auth?.isAuthenticated && (
          <div className="mb-2 text-muted">請先登入以使用簽到功能。</div>
        )}
      </div>

      {isLightboxOpen && activePhoto && (
        <div
          className="lightbox-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="地點相片檢視"
          onClick={closeLightbox}
        >
          <button
            type="button"
            className="lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            aria-label="關閉圖片檢視"
          >
            ×
          </button>

          {galleryPhotos.length > 1 && (
            <>
              <button
                type="button"
                className="lightbox-nav lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  showPrevPhoto();
                }}
                aria-label="上一張"
              >
                ‹
              </button>
              <button
                type="button"
                className="lightbox-nav lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  showNextPhoto();
                }}
                aria-label="下一張"
              >
                ›
              </button>
            </>
          )}

          <div
            className="lightbox-content"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <img
              src={activePhoto.fullImage}
              alt={activePhoto.name}
              className="lightbox-image"
            />
            <div className="lightbox-caption">
              {activePhotoIndex + 1} / {galleryPhotos.length}
            </div>
            <div className="lightbox-meta">
              {activePhoto.uploaderName && <div className="lightbox-meta-uploader">上傳者：{activePhoto.uploaderName}</div>}
              {activePhoto.uploadedAt && (
                <div className="lightbox-meta-time">上傳時間：{new Date(activePhoto.uploadedAt).toLocaleString()}</div>
              )}
              {canDeleteActivePhoto && (
                <div className="lightbox-meta-actions">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDeletePhoto}
                    disabled={deletingPhoto}
                  >
                    {deletingPhoto ? '刪除中…' : '刪除此相片'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default LocationDetailPage;
